import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BriefcaseIcon, LayoutDashboardIcon, PackageIcon, UsersIcon, SparklesIcon, BotIcon, ArrowRightIcon } from 'lucide-react';
import { useBusiness, CATALOG_LABEL, BUSINESS_TYPE_LABEL } from './BusinessContext';
type ModuleCard = {
  icon: React.ElementType;
  title: string;
  description: string;
  accent: string;
};
export function BusinessHubWelcomeModal() {
  const {
    hub,
    markHubSeen
  } = useBusiness();
  const navigate = useNavigate();
  const open = Boolean(hub?.active && hub?.isNew);
  const handleStart = () => {
    markHubSeen();
    navigate('/business');
  };
  const mountNode = typeof document !== 'undefined' && document.getElementById('app-screen-root') || (typeof document !== 'undefined' ? document.body : null);
  if (!hub || !mountNode) return null;
  const catalogLabel = CATALOG_LABEL[hub.type] ?? 'Catalog';
  const typeLabel = BUSINESS_TYPE_LABEL[hub.type] ?? 'Business';
  const modules: ModuleCard[] = [{
    icon: LayoutDashboardIcon,
    title: 'Dashboard',
    description: 'See performance, leads and tasks at a glance.',
    accent: 'text-primary-indigo bg-primary-indigo/10'
  }, {
    icon: PackageIcon,
    title: catalogLabel,
    description: 'Add inventory by chat, file or link — auto-categorized.',
    accent: 'text-ai-purple bg-ai-purple/10'
  }, {
    icon: UsersIcon,
    title: 'Leads',
    description: 'Capture and track every buyer enquiry in one place.',
    accent: 'text-electric-cyan bg-electric-cyan/10'
  }, {
    icon: BotIcon,
    title: 'AI Channels',
    description: 'An AI assistant that answers buyers 24/7 on your website widget & WhatsApp.',
    accent: 'text-ai-purple bg-ai-purple/10'
  }];
  return createPortal(<AnimatePresence data-id="element-646">
      {open && <div className="absolute inset-0 z-[100] flex flex-col justify-end" role="dialog" aria-modal="true" aria-labelledby="hub-welcome-title" data-id="element-647">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-dark-navy/60 backdrop-blur-sm" data-id="element-648" />

          <motion.div initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 320,
        damping: 32
      }} className="relative w-full bg-white rounded-t-3xl shadow-2xl overflow-hidden flex flex-col max-h-[88%]" data-id="element-649">
            {/* Header */}
            <div className="relative px-5 pt-3 pb-5 bg-primary-indigo text-white overflow-hidden flex-shrink-0" data-id="element-650">
              <div className="w-10 h-1 bg-white/30 rounded-full mx-auto mb-4" data-id="element-651" />
              <div className="flex items-center gap-3" data-id="element-652">
                <motion.div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center flex-shrink-0" initial={{
              rotate: -8,
              scale: 0.8
            }} animate={{
              rotate: 0,
              scale: 1
            }} transition={{
              delay: 0.1,
              type: 'spring',
              stiffness: 200
            }} data-id="element-653">
                  <BriefcaseIcon className="w-6 h-6" data-id="element-654" />
                </motion.div>
                <div className="min-w-0" data-id="element-655">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-white/70" data-id="element-656">
                    {typeLabel} workspace
                  </p>
                  <h2 id="hub-welcome-title" className="text-lg font-bold leading-tight" data-id="element-657">
                    Welcome to your Business Hub
                    {hub.name ? `, ${hub.name}` : ''}
                  </h2>
                </div>
              </div>
              <p className="mt-3 text-[13px] text-white/80 leading-relaxed" data-id="element-658">
                Everything you need to sell smarter is ready. Here's what each
                module does for you.
              </p>
            </div>

            {/* Module list */}
            <div className="px-5 py-4 space-y-2.5 overflow-y-auto flex-1" data-id="element-659">
              {modules.map((m, i) => <motion.div key={m.title} className="flex items-start gap-3 p-3 rounded-2xl border border-slate-100 bg-slate-50/60" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.12 + i * 0.05
          }} data-id="element-660">
                  <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${m.accent}`} data-id="element-661">
                    <m.icon className="w-5 h-5" data-id="element-662" />
                  </div>
                  <div className="min-w-0" data-id="element-663">
                    <p className="text-sm font-semibold text-slate-900" data-id="element-664">
                      {m.title}
                    </p>
                    <p className="text-xs text-slate-500 leading-snug mt-0.5" data-id="element-665">
                      {m.description}
                    </p>
                  </div>
                </motion.div>)}
            </div>

            {/* Footer */}
            <div className="px-5 pt-3 pb-7 border-t border-slate-100 flex-shrink-0 bg-white" data-id="element-666">
              <button onClick={handleStart} className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-primary-indigo text-white font-semibold shadow-[0_8px_24px_rgb(67,56,202,0.35)] active:scale-[0.98] transition-transform" data-id="element-667">
                Get started
                <ArrowRightIcon className="w-4 h-4" data-id="element-668" />
              </button>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>, mountNode);
}